import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Nullable, SelectOption } from 'global';
import { UserProfile } from '@trnsact/trnsact-shared-types';
import { ListingForm } from 'components/shared/TableListing';
import { Box, Button, Stack, Alert, Divider } from '@mui/material';
import { constructorSubmitLabelByMode } from 'modules/stages/constants';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';

import { Template } from '../../api';
import { useBPFMutations } from '../../api';
import { stagesMapper } from '../../mappers';
import { StagesConstructorForm } from './form/StagesConstructorForm';
import { StagesConstructorListing } from './listing/StagesConstructorListing';
import { StagesFormValues, TemplateConstructorMode, TemplateFormValues } from '../../types';

interface Props {
  accountId: string;
  template: Nullable<Template>;
  isDepartmentsLoading: boolean;
  mode: TemplateConstructorMode;
  departmentsOptions: SelectOption[];
}

export const StagesConstructor = ({ accountId, template, departmentsOptions, isDepartmentsLoading, mode }: Props) => {
  const { goBack } = useHistory();

  const {
    isTemplateCreating,
    isTemplateCopying,
    isTemplateUpdating,
    handleCreateTemplate,
    handleCopyTemplate,
    handleUpdateTemplate,
  } = useBPFMutations();

  const templateSubmitRef = useRef<{ submit: () => Promise<Nullable<TemplateFormValues>> } | null>(null);
  const stagesSubmitRef = useRef<{ submit: () => Promise<Nullable<ListingForm<StagesFormValues>>> } | null>(null);

  const account = useSelector((state: any) => state.account);
  const relatedUsersMap = new Map<string, UserProfile>((account.contacts ?? []).map((u: UserProfile) => [u.id, u]));

  const handleSubmitAll = async () => {
    const templateValues = await templateSubmitRef.current?.submit();
    const stagesValues = await stagesSubmitRef.current?.submit();

    if (templateValues && stagesValues) {
      const createOrUpdateDto = stagesMapper.processFormValuesToCreateOrUpdateDto({
        mode,
        accountId,
        stagesValues,
        templateValues,
      });

      const handlersMapperByMode: Record<TemplateConstructorMode, () => Promise<void>> = {
        copy: () =>
          handleCopyTemplate({
            sourceTemplateId: template!.businessProcessFlowId,
            newTemplateDto: createOrUpdateDto,
          }),
        create: () => handleCreateTemplate(createOrUpdateDto),
        edit: () =>
          handleUpdateTemplate({ ...createOrUpdateDto, businessProcessFlowId: template!.businessProcessFlowId }),
      };

      await handlersMapperByMode[mode]();
    }
  };

  return (
    <CardContainer>
      {mode === 'edit' && (
        <Alert color="info">
          You only have access to change template names, stage names, and notification recipients. If you need to make
          other changes, create a new template and mark it as active
        </Alert>
      )}

      <StagesConstructorForm
        mode={mode}
        template={template}
        submitRef={templateSubmitRef}
        departmentsOptions={departmentsOptions}
        isDepartmentsLoading={isDepartmentsLoading}
      />

      <Divider />

      <Box sx={{ overflowY: 'auto', height: '100%' }}>
        <StagesConstructorListing
          isEdit={mode === 'edit'}
          submitRef={stagesSubmitRef}
          initialValues={stagesMapper.processTemplateToConstructorFormValues(template, relatedUsersMap)}
        />
      </Box>

      <Stack direction="row" gap="1rem">
        <Button
          size="large"
          variant="contained"
          onClick={handleSubmitAll}
          loading={isTemplateCreating || isTemplateCopying || isTemplateUpdating}
        >
          {constructorSubmitLabelByMode[mode]}
        </Button>

        <Button onClick={goBack} size="large" disabled={isTemplateCreating || isTemplateCopying || isTemplateUpdating}>
          Cancel
        </Button>
      </Stack>
    </CardContainer>
  );
};
